/**
 * ExportsCard — two download buttons (PDF, DXF) for a lead. Each button
 * generates the export client-side and triggers a browser download. Per-button
 * loading state; errors shown inline; only one export runs at a time.
 *
 * NOTE: the BOM (.csv) materials-list export is intentionally parked for a
 * later version — we want a real, order-ready materials list before exposing
 * it to builders. The generator (lib/exports/bom.ts) and its tests stay in
 * place; re-add the ExportButton + the "bom" branch in handleExport to restore.
 */
import { useState } from "react";
import type { LeadDetail, TenantConfig } from "../lib/api";
import { generatePdf } from "../lib/exports/pdf";
import { generateDxf } from "../lib/exports/dxf";
import { StatusMessage } from "./StatusMessage";
import ui from "../styles/ui.module.css";
import styles from "./ExportsCard.module.css";

interface Props {
  lead: LeadDetail;
  tenant: TenantConfig | null;
}

type ExportFormat = "pdf" | "dxf";

export function ExportsCard({ lead, tenant }: Props) {
  const [generating, setGenerating] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<{ format: ExportFormat; message: string } | null>(null);

  const handleExport = async (format: ExportFormat) => {
    setGenerating(format);
    setError(null);
    try {
      let blob: Blob;
      let ext: string;
      if (format === "pdf") {
        if (!tenant) {
          throw new Error("Tenant info not loaded — try again.");
        }
        const pdf = generatePdf({ designState: lead.designState, tenant });
        if (!pdf) throw new Error("No design data to export.");
        blob = pdf;
        ext = "pdf";
      } else {
        // round9/embed-config — pass the tenant's custom slip sizes so a lead
        // using one exports WITH its slip (productName deliberately not passed:
        // the DXF title block is unchanged from before this round).
        const dxf = generateDxf(lead.designState, { customBoatBays: tenant?.customBoatBays });
        if (!dxf) throw new Error("No design data to export.");
        blob = dxf;
        ext = "dxf";
      }
      const filename = `dockpro-${lead.leadId}-${format}.${ext}`;
      triggerDownload(blob, filename);
    } catch (err) {
      setError({
        format,
        message: err instanceof Error ? err.message : "Export failed",
      });
    } finally {
      setGenerating(null);
    }
  };

  return (
    <section className={ui.card}>
      {/* Round 6 polish audit (AUD-016): promoted h3->h2 so this doesn't skip
          a level under LeadDetailPage's own h1 (the homeowner's name). */}
      <h2 className={ui.cardTitle}>Exports</h2>
      <p className={ui.cardHint}>
        Plan documents and CAD files for this lead.
      </p>
      <div className={styles.buttons}>
        <ExportButton
          label="PDF"
          sublabel="Plan + estimate"
          onClick={() => handleExport("pdf")}
          isLoading={generating === "pdf"}
          disabled={generating !== null || !tenant}
        />
        <ExportButton
          label="DXF"
          sublabel="CAD file"
          onClick={() => handleExport("dxf")}
          isLoading={generating === "dxf"}
          disabled={generating !== null}
        />
      </div>
      {error && (
        <StatusMessage
          variant="error"
          message={`${error.format.toUpperCase()} export failed: ${error.message}`}
          className={`${ui.error} ${styles.errorMessage}`}
        />
      )}
    </section>
  );
}

function ExportButton({ label, sublabel, onClick, isLoading, disabled }: {
  label: string;
  sublabel: string;
  onClick: () => void;
  isLoading: boolean;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      className={styles.button}
      onClick={onClick}
      disabled={disabled}
    >
      <span className={styles.buttonLabel}>
        {isLoading ? "Generating…" : label}
      </span>
      <span className={styles.buttonSublabel}>{sublabel}</span>
    </button>
  );
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Slight delay before revoking — some browsers race on revoke.
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
